import { makeAutoObservable } from 'mobx';

import { ExpectedDeliveryDateStatus } from '../../Purchase/Checkout/Delivery/DeliveryMethods/Common/ExpectedDelivery/ExpectedDeliveryDateStatus.csharp';
import { ExpectedDeliveryDateViewModel } from '../../Purchase/Checkout/Delivery/DeliveryMethods/Common/ExpectedDelivery/ExpectedDeliveryDateViewModel.csharp';
import { DeliveryMethod } from '../../Purchase/Checkout/Delivery/DeliveryMethods/DeliveryMethod.csharp';
import { ExpectedDeliveryCacheModel, ExpectedDeliveryDateCache } from './ExpectedDeliveryDateCache';

class ExpectedDeliveryDate {
    private _expectedDeliveryDateCache = new ExpectedDeliveryDateCache();
    private _availableForProduct: DeliveryMethod[] = [];
    public postalCode: string | null = null;
    public selectedDeliveryMethod: ExpectedDeliveryDateViewModel | null = null;
    public deliveryMethods: ExpectedDeliveryCacheModel | null = null;

    constructor() {
        makeAutoObservable(this);

        this.postalCode = this._expectedDeliveryDateCache.getPostalCode();
        this.deliveryMethods = this._expectedDeliveryDateCache.getMethods();
        this.selectedDeliveryMethod = this._expectedDeliveryDateCache.getSelectedMethod();
    }

    public get fastestDeliveryMethod(): ExpectedDeliveryDateViewModel | null {
        if (!this.deliveryMethods) return null;

        const sortedByDeliveryDate = [...this.availableDeliveryMethods].sort((a, b) => (a.expectedDate > b.expectedDate ? 1 : -1));
        return sortedByDeliveryDate[0];
    }

    public get allDeliveryMethods(): ExpectedDeliveryDateViewModel[] {
        return (
            this.deliveryMethods?.methods.slice().sort((a, b) => {
                const isFirstMethodAvailable = this.isDeliveryMethodAvailable(a);
                const isSecondMethodAvailable = this.isDeliveryMethodAvailable(b);

                if (isFirstMethodAvailable && isSecondMethodAvailable) {
                    return new Date(a.expectedDate).getTime() - new Date(b.expectedDate).getTime();
                }

                return isFirstMethodAvailable ? -1 : 1;
            }) || []
        );
    }

    public get availableDeliveryMethods(): ExpectedDeliveryDateViewModel[] {
        return (
            this.deliveryMethods?.methods?.filter(
                (x) => x.status === ExpectedDeliveryDateStatus.Ok && x.expectedDate !== null && this._availableForProduct.includes(x.method)
            ) || []
        );
    }

    public setMethodsAvailableForProduct = (methods: DeliveryMethod[]) => {
        this._availableForProduct = methods;
    };

    public isDeliveryMethodAvailable(method: ExpectedDeliveryDateViewModel) {
        return !!this.availableDeliveryMethods?.find((x) => x.methodName === method?.methodName);
    }

    public selectDeliveryMethod = (selectedDeliveryMethod: ExpectedDeliveryDateViewModel) => {
        this.selectedDeliveryMethod = selectedDeliveryMethod;
        this._expectedDeliveryDateCache.saveSelectedMethod(selectedDeliveryMethod);
    };

    public unselectDeliveryMethod = () => {
        this.selectedDeliveryMethod = null;
        this._expectedDeliveryDateCache.removeSelectedMethod();
    };

    public setPostalCode = (postalCode: string) => {
        this.postalCode = postalCode;
        this._expectedDeliveryDateCache.savePostalCode(this.postalCode);
    };

    public setDeliveryMethods = (deliveryMethods: ExpectedDeliveryDateViewModel[], postalCode: string | null) => {
        this.deliveryMethods = { postalCode, methods: deliveryMethods };
        this._expectedDeliveryDateCache.saveMethods(this.deliveryMethods);
    };
}

export const expectedDeliveryDate = new ExpectedDeliveryDate();
